import React, { useEffect, useRef, useState, useCallback } from 'react'
import type { FC, DragEvent, ChangeEvent } from 'react'
import { CircularProgress } from '@material-ui/core'
import Close from '@material-ui/icons/Close'
import { createStyles, makeStyles } from '@material-ui/core/styles'

import { Button } from '@homeviews/shared-ui'
import {
  useLazyGetOutreachDocumentQuery,
  useCreateOutreachDocumentMutation,
} from '@homeviews/store'
import { mixpanelTrack } from '@homeviews/seo'
import { Typography, UploadTooltip } from '@homeviews/outreach'

const KILO_BYTE = 1024
const MEGA_BYTE = KILO_BYTE * KILO_BYTE
const ERROR_RED = '#DC3545'

const useStyles = makeStyles((theme) =>
  createStyles({
    wideWrapper: {
      width: '100%',
      padding: '17px 0 57px 20px',
      borderRadius: '5px',
      border: 'dashed 1px #e3e3e3',
      backgroundColor: '#fff',
    },
    wideWrapperDragOver: {
      backgroundColor: theme.colors.bgColor,
    },
    customH2: {
      '.MuiTypography-h2&': {
        display: 'inline',
        paddingRight: '6px',
        verticalAlign: 'bottom',
      },
    },
    wideInnerWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px 0 0 0',
    },
    customBody1Wide: {
      '.MuiTypography-body1&': {
        fontSize: '14px',
        margin: '10px 0 16px 0',
      },
    },
    selectFileLabel: {
      padding: '12px 32px',
      margin: '0 0 16px 0',
    },
    customBody2Wide: {
      '.MuiTypography-body2&': {
        fontSize: '12px',
      },
    },
    labelWrapper: {
      width: '280px',
      display: 'flex',
      justifyContent: 'space-between',
    },
    customBody1Thin: {
      '.MuiTypography-body1&': {
        display: 'inline-block',
        fontSize: '14px',
        fontWeight: '700',
        lineHeight: '22px',
        letterSpacing: '0.1px',
        margin: '0 0 6px 0',
      },
    },
    tooltipWrapper: {
      marginBottom: '4px',
    },
    uploadButton: {
      width: '280px',
      height: '40px',
      margin: '0 0 4px 0',
      color: theme.colors.grey600,
      borderColor: theme.colors.grey600,
    },
    uploadButtonTextValidating: {
      verticalAlign: 'super',
      marginLeft: '6px',
    },
    uploadButtonError: {
      borderColor: ERROR_RED,
    },
    customBody2ThinError: {
      maxWidth: '350px',
      '.MuiTypography-body2&': {
        color: ERROR_RED,
      },
    },
    anchorError: {
      fontWeight: 700,
      color: ERROR_RED,
    },
    selectedFileWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '9px 8px 8px 15px',
      marginBottom: '4px',
      fontSize: '14px',
      border: `1px solid ${theme.colors.fieldBorderColor}`,
      borderRadius: '5px',
      color: theme.colors.grey480,
    },
    deleteFileButton: {
      padding: '0',
      border: 'none',
      background: '0 0',
      appearance: 'none',
      fontSize: '21px',
      lineHeight: 0,
      cursor: 'pointer',
      color: theme.colors.grey480,
    },
  }),
)

type DocumentErrorResponse = {
  data: {
    errors: {
      document: Array<string>
    }
  }
  status: number
}

type UploadFileProps = {
  inputId: string
  showLabel?: boolean
  wideUntilFileChanged?: boolean
  fileChangedCallback?: () => void
  setDocumentIdCallback: (fileId: number) => void
  deleteDocumentIdCallback: () => void
}

export const UploadFile: FC<UploadFileProps> = ({
  inputId,
  showLabel = false,
  wideUntilFileChanged = false,
  fileChangedCallback,
  setDocumentIdCallback,
  deleteDocumentIdCallback,
}) => {
  const [errorString, setErrorString] = useState('')
  const [selectedFileName, setSelectedFileName] = useState('')
  const [fileChanged, setFileChanged] = useState(false)
  const [validating, setValidating] = useState(false)
  const setDocumentIdCallable = useRef(true)
  const [dragOver, setDragOver] = useState(false)
  const classes = useStyles()
  const acceptedFileMimeTypes = [
    'text/csv', // CSV
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
  ]

  const inputFileEl = useRef<HTMLInputElement>(null)
  const clearInputFileElValue = () =>
    inputFileEl.current !== null && (inputFileEl.current.value = '')
  const InputFile = () => (
    <input
      ref={inputFileEl}
      hidden
      type="file"
      id={inputId}
      name={inputId}
      accept={acceptedFileMimeTypes.join(',')}
      onChange={handleOnChangeInputFile}
    />
  )

  const resetStates = useCallback(() => {
    clearInputFileElValue() // Clear HTML Input as causes same-file-name-2nd-time issues
    setSelectedFileName('') // Clear any file name
    setValidating(false) // Stop showing validation spinner
  }, [])
  const resetStatesAndSetErrorString = useCallback(
    (error?: string | null) => {
      resetStates()
      setErrorString(error || 'An unknown error has occurred.') // Specific or generic
    },
    [resetStates],
  )

  useEffect(() => {
    if (errorString)
      mixpanelTrack('Outreach: Upload Error Message Displayed', {
        Message: errorString,
      })
  }, [errorString])

  const [getDocument, result] = useLazyGetOutreachDocumentQuery()
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    // When API has returned data for getDocument, and is not fetching:
    if (!result.isFetching && result.data) {
      const { id, status, message } = result.data.data
      // When API says document has an error, then stop validation spinner,
      // show the error message if present, and stop polling getDocument.
      if (status === 'error') {
        resetStatesAndSetErrorString(message)
      }
      // When API says document is valid, then stop validation spinner,
      // show document file name, and pass valid documentId to parent.
      else if (status === 'validated') {
        setValidating(false)
        if (setDocumentIdCallable.current) {
          setDocumentIdCallback(id)
          setDocumentIdCallable.current = false // Make sure we only setDocumentId once
          mixpanelTrack('Outreach: Upload File Success')
        }
      } else {
        // Schedule getDocument request re-run as BE is still processing the document.
        // getDocument will update result, which will execute this useEffect again,
        // so if a stopping-status is encountered we stop, otherwise keep looping.
        timeoutId = setTimeout(() => void getDocument({ documentId: id }), 3000)
      }
    }
    return () => clearTimeout(timeoutId) // Clear timeout if Component Unmounts
  }, [result, resetStatesAndSetErrorString, setDocumentIdCallback, getDocument])

  const [createDocument] = useCreateOutreachDocumentMutation()
  const setFile = async (file: File) => {
    // If wide, only until first file is changed
    if (wideUntilFileChanged && !fileChanged) {
      setFileChanged(true)
      // Call parent fileChangedCallback so parent knows file changed
      if (fileChangedCallback) fileChangedCallback()
    }

    // Validate file types and file size
    if (!acceptedFileMimeTypes.includes(file.type)) {
      resetStatesAndSetErrorString(
        `File "${file.name}" has an unsupported file type, must be CSV or XLSX.`,
      )
      return
    }
    if (file.size > MEGA_BYTE * 3) {
      resetStatesAndSetErrorString(
        `File "${file.name}" is too large to be uploaded, must be smaller than 4MB.`,
      )
      return
    }

    setValidating(true) // Start showing validating spinner
    setSelectedFileName(file.name) // Shows when valid document
    setDocumentIdCallable.current = true // Let setDocumentIdCallback be called again

    // Prepare FormData data structure that API expects from File
    const documentFormData = new FormData()
    documentFormData.append('document', file)

    try {
      // CreateDocument in API which we will reference by documentId hereon
      const response = await createDocument({ documentFormData }).unwrap()
      // If no error, immediately call getDocument to begin validation polling loop
      void getDocument({ documentId: response.data.id })
    } catch (err) {
      const errResponse = err as DocumentErrorResponse
      if (errResponse.status === 422) {
        const errorStr = errResponse.data.errors.document.join(' ')
        resetStatesAndSetErrorString(errorStr)
      } else {
        resetStatesAndSetErrorString()
      }
    }
  }

  const handleOnChangeInputFile = (e: ChangeEvent<HTMLInputElement>) => {
    setErrorString('') // Clear any previous error
    const tFiles = e.target.files
    // Check length, as File Picker Cancel does invoke onChange
    if (tFiles && tFiles.length !== 0) {
      const file = tFiles[0]
      void setFile(file)
    }
  }

  const handleOnClickDelete = () => {
    resetStates()
    deleteDocumentIdCallback() // Call parent callback so parent discards documentId
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(true)
  }
  const handleDropWideWrapper = (e: DragEvent<HTMLDivElement>) => {
    // Prevent default behavior (Prevent file from being opened)
    e.preventDefault()
    // Use DataTransferItemList interface to access the first file
    const dTItems = e.dataTransfer.items
    if (dTItems) {
      // If dropped item isn't a file, do nothing
      if (dTItems[0].kind === 'file') {
        const file = dTItems[0].getAsFile()
        if (file) void setFile(file)
      }
    }
  }
  const handleDragLeave = () => setDragOver(false)
  const handleDragEnd = () => setDragOver(false)

  const wideWrapperClassNames = () =>
    `${classes.wideWrapper} ${dragOver ? classes.wideWrapperDragOver : ''}`

  const uploadButtonClassNames = () =>
    `${classes.uploadButton} ${errorString && classes.uploadButtonError}`
  const uploadButtonContent = () => {
    if (!validating) return 'Upload File'
    else
      return (
        <>
          <CircularProgress size="20px" />
          <span className={classes.uploadButtonTextValidating}>
            Validating File
          </span>
        </>
      )
  }

  if (wideUntilFileChanged && !fileChanged) {
    return (
      <div
        className={wideWrapperClassNames()}
        onDragOver={handleDragOver}
        onDrop={handleDropWideWrapper}
        onDragEnd={handleDragEnd}
        onDragLeave={handleDragLeave}
      >
        <Typography variant="h2" className={classes.customH2}>
          Upload
        </Typography>
        <UploadTooltip />
        <div className={classes.wideInnerWrapper}>
          <img
            src="assets/images/outreach-upload.png"
            alt="upload"
            width="46"
            height="46"
          />
          <Typography variant="body1" className={classes.customBody1Wide}>
            Select file or drag and drop to upload
          </Typography>
          <InputFile />
          <Button
            customVariant="outlinedPrimary"
            className={classes.selectFileLabel}
            component="label"
            htmlFor={inputId}
          >
            Select file for upload
          </Button>
          <Typography variant="body2" className={classes.customBody2Wide}>
            Accepted formats: .CSV .XLSX
          </Typography>
          <Typography variant="body2" className={classes.customBody2Wide}>
            Required Fields: First name, Email Address
          </Typography>
        </div>
      </div>
    )
  } else {
    return (
      <div>
        <div className={classes.labelWrapper}>
          <Typography variant="body1" className={classes.customBody1Thin}>
            {showLabel && 'Upload file'}
          </Typography>
          <div className={classes.tooltipWrapper}>
            <UploadTooltip />
          </div>
        </div>
        <InputFile />
        {!selectedFileName || validating ? (
          <Button
            customVariant="outlinedPrimary"
            className={uploadButtonClassNames()}
            component="label"
            htmlFor={inputId}
            disabled={validating}
          >
            {uploadButtonContent()}
          </Button>
        ) : (
          <div className={classes.selectedFileWrapper}>
            <span>{selectedFileName}</span>
            <button
              className={classes.deleteFileButton}
              onClick={handleOnClickDelete}
            >
              <Close fontSize="inherit" />
            </button>
          </div>
        )}
        {errorString ? (
          <Typography variant="body2" className={classes.customBody2ThinError}>
            {errorString} For help and advice, see the article in the Knowledge
            Base{' '}
            <b>
              <a
                className={classes.anchorError}
                href="https://knowledge.homeviews.com/knowledge/why-did-i-get-an-error-message-when-i-uploaded-my-spreadsheet-of-emails"
                target="_blank"
                rel="noreferrer"
              >
                here
              </a>
            </b>
            .
          </Typography>
        ) : null}
      </div>
    )
  }
}
